'use client'

import type { ColumnFiltersState } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, RefreshCw, X } from 'lucide-react'

import type { RenderLink } from '@ui/web/utils'
import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../../../context'
import { CLASS_FILTER_COLUMN, StudentTable } from '../../../tables'
import { Empty } from './empty'

export interface StudentsTabProps {
  type: 'web' | 'school'
  /** Student name → their profile. Receives `meta.childId`. */
  renderProfileLink: RenderLink
  /** Empty-state CTAs. Receives `meta.teamId` for "Kelola kelas", nothing for "Daftarkan siswa". */
  renderCreateLink?: RenderLink
  /** Empty-state "Jelajahi beranda" CTA. */
  renderExploreLink?: RenderLink
}

/**
 * "Siswa saya" (school) / "Anak saya" (family) — the roster, scoped to what the signed-in viewer is
 * allowed to see.
 *
 * Owner and admin get every student and every class in the filter. A member gets only the students
 * in the classes they are assigned to, which is the same rule the other dashboard tabs apply, and
 * the class filter is narrowed to match so the dropdown never offers a class they cannot open.
 *
 * The table itself is the shared roster used by Kelola murid — same columns, same inline editors,
 * same row actions — so a student edited here behaves exactly as it does there.
 */
export function StudentsTab({ type, renderProfileLink, renderCreateLink, renderExploreLink }: StudentsTabProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()

  // A family has no rombel and no tingkat, and its "roster" is a handful of the parent's own
  // children — so the class column, the class filter, and the teacher-facing WhatsApp message all
  // drop away, and the row keeps the full account menu a parent needs.
  const isFamily = type === 'web'

  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()
  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: !isAdminOrOwner && !isRolePending,
  })

  const {
    data: listChildren,
    isPending: isPendingChildren,
    isError: isErrorChildren,
    refetch: refetchChildren,
  } = useQuery(
    trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
    }),
  )

  /** Class filter is owned here so the "no students in this class" empty can name the class. */
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const myTeams = useMemo(() => myTeamsQuery.data ?? [], [myTeamsQuery.data])
  const isMemberWithoutClass = !isRolePending && !isAdminOrOwner && !myTeamsQuery.isLoading && myTeams.length === 0

  /**
   * A member sees only the students in their own classes. Done here rather than server-side because
   * `listChildren` is the school-wide roster every dashboard tab already shares — see the identical
   * narrowing in the school dashboard's `filteredChildIds`.
   */
  const students = useMemo(() => {
    const all = listChildren?.data ?? []
    if (isAdminOrOwner) return all
    if (myTeams.length === 0) return []
    const myTeamIds = new Set(myTeams.map((team) => team.id))
    return all.filter((student) => student.schoolClasses?.some((schoolClass) => myTeamIds.has(schoolClass.id)))
  }, [listChildren?.data, isAdminOrOwner, myTeams])

  const classFilter = columnFilters.find((filter) => filter.id === CLASS_FILTER_COLUMN)?.value as string | undefined

  /** Rows the table will actually show, so the empty state can distinguish "no rows" from "no match". */
  const studentsInClass = useMemo(() => {
    if (!classFilter) return students
    if (classFilter === NO_SCHOOL_CLASS_ID) {
      return students.filter((student) => (student.schoolClasses?.length ?? 0) === 0)
    }
    return students.filter((student) => student.schoolClasses?.some((schoolClass) => schoolClass.id === classFilter))
  }, [students, classFilter])

  const selectedClassName = useMemo(() => {
    if (!classFilter) return null
    if (classFilter === NO_SCHOOL_CLASS_ID) return 'Tanpa kelas'
    const fromRoster = students
      .flatMap((student) => student.schoolClasses ?? [])
      .find((schoolClass) => schoolClass.id === classFilter)
    return fromRoster?.name ?? myTeams.find((team) => team.id === classFilter)?.name ?? null
  }, [classFilter, students, myTeams])

  if (isPendingChildren || isRolePending || !memberRole || myTeamsQuery.isLoading) {
    return (
      <Shell type={type}>
        <Skeleton className='srd:h-[500px] srd:w-full' />
      </Shell>
    )
  }

  if (isErrorChildren) {
    return (
      <Shell type={type}>
        <section className='srd:flex srd:h-[300px] srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4'>
          <p
            className={cn(
              typographyVariants({ variant: 'body-md' }),
              'srd:text-error srd:flex srd:items-center srd:gap-x-2',
            )}
          >
            <X className='srd:size-4' />
            Gagal mengambil data murid
          </p>
          <Button size='sm' variant='outline' className='srd:gap-x-2' onClick={() => void refetchChildren()}>
            <RefreshCw className='srd:size-3.5' />
            Coba lagi
          </Button>
        </section>
      </Shell>
    )
  }

  if (isMemberWithoutClass) {
    return (
      <Shell type={type}>
        <Empty type={type} noClass />
      </Shell>
    )
  }

  if (students.length === 0) {
    return (
      <Shell type={type}>
        <Empty type={type} noStudents renderCreateLink={renderCreateLink} renderExploreLink={renderExploreLink} />
      </Shell>
    )
  }

  // A class filter that empties the roster is the "fill this class" moment, not a failed search.
  if (classFilter && studentsInClass.length === 0) {
    return (
      <Shell type={type}>
        <Empty
          type={type}
          renderCreateLink={renderCreateLink}
          schoolClassName={selectedClassName}
          teamId={classFilter === NO_SCHOOL_CLASS_ID ? null : classFilter}
        />
      </Shell>
    )
  }

  return (
    <Shell type={type}>
      <StudentTable
        data={students}
        type={type}
        // Every student on this tab is one the viewer teaches, so a guru kelas may edit their
        // account. Class membership is the exception — `setChildTeams` stays owner/admin, so the
        // inline Kelas cell is read-only for them and moving a student happens on the class page.
        canEdit
        canEditClass={isAdminOrOwner}
        // School: one verb, because premium seats and removing a student are owner/admin jobs that
        // live on Kelola murid. Family: the parent owns all three, so they keep the full menu.
        rowAction={isFamily ? 'menu' : 'settings'}
        pinAction={isFamily ? 'copy' : 'menu'}
        // "Orang tua" is a school question — a family roster is already the parent's own. The school
        // set is spelled out rather than defaulted so adding a column elsewhere cannot land here.
        columns={
          isFamily ? ['name', 'pin', 'access'] : ['name', 'pin', 'schoolClass', 'schoolGrade', 'parent', 'access']
        }
        // No "Tingkat" select: the dashboard roster filters by class and access only, matching the
        // two questions a teacher opens this tab with — which class, and who is on Premium.
        filters={isFamily ? ['access'] : ['schoolClass', 'access']}
        searchPlaceholder='Cari'
        footer={
          isFamily && renderCreateLink ? (
            <div className='srd:bg-blue-10 srd:text-neutral-black srd:w-full srd:p-4'>
              <Button variant='ghost' className='srd:h-fit srd:w-fit srd:p-0' asChild>
                {renderCreateLink({
                  children: (
                    <>
                      <span className='srd:bg-blue-100 srd:flex srd:size-5 srd:shrink-0 srd:items-center srd:justify-center srd:rounded-full'>
                        <Plus className='srd:text-neutral-white srd:size-3' />
                      </span>
                      <span className={cn(typographyVariants({ variant: 'label-md' }))}>Tambah anak</span>
                    </>
                  ),
                  className: 'srd:flex srd:h-fit srd:w-fit srd:items-center srd:gap-x-2',
                })}
              </Button>
            </div>
          ) : null
        }
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        renderStudentName={({ student, className }) => (
          <>
            {renderProfileLink({
              children: student.name,
              className: cn(className, 'srd:hover:underline'),
              meta: { childId: student.id },
            })}
          </>
        )}
      />
    </Shell>
  )
}

/** Same width and gutter as every other dashboard tab, so the roster lines up with the charts. */
function Shell({ type, children }: { type: 'web' | 'school'; children: ReactNode }) {
  return (
    <div className='srd:mx-auto srd:w-full srd:max-w-[1200px] srd:px-4 srd:flex srd:flex-col srd:gap-y-4'>
      <h2 className={cn(typographyVariants({ variant: 'title-md' }))}>{type === 'web' ? 'Anak Saya' : 'Murid Saya'}</h2>

      {children}
    </div>
  )
}
